import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import SearchIcon from "@material-ui/icons/Search";
import PlayIcon from "@material-ui/icons/PlayCircleOutlineRounded";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import NodeIcon from "./NodeIcon";

const useStyles = makeStyles(theme => ({
  note: { display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: theme.app.textTertiary, marginBottom: 8 },
  noteDot: { width: 5, height: 5, borderRadius: "50%", background: theme.app.ok, flexShrink: 0 },
  list: { flex: 1, minHeight: 0, overflow: "auto", marginTop: 10 },
  // cabeçalho do grupo = o nó de origem (idêntico ao chip do canvas); clicável p/ expandir
  groupHead: { display: "flex", alignItems: "center", gap: 9, padding: "8px 4px", cursor: "pointer", borderRadius: 8, "&:hover": { background: theme.app.hover } },
  caret: { width: 12, color: theme.app.textTertiary, fontSize: 11, flexShrink: 0, userSelect: "none" },
  groupTitle: { fontSize: 12.5, fontWeight: 700, color: theme.app.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  groupTag: { fontFamily: theme.app.mono, fontSize: 10, color: theme.app.textTertiary, marginLeft: "auto", whiteSpace: "nowrap", flexShrink: 0 },
  // folha (campo) — MESMO padrão da árvore: chave mono cinza + valor em destaque
  leaf: { display: "flex", alignItems: "center", borderRadius: 6, padding: "4px 6px", paddingLeft: 47, cursor: "pointer", "&:hover": { background: theme.app.hover } },
  key: { fontFamily: theme.app.mono, fontSize: 12.5, fontWeight: 400, color: theme.app.ink, flexShrink: 0 },
  val: { fontFamily: theme.app.mono, fontSize: 12.5, fontWeight: 400, color: theme.app.ok, marginLeft: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  empty: { fontSize: 12, fontStyle: "italic", color: theme.app.textTertiary, marginLeft: 12 },
  invite: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.app.textSecondary, background: "#FAFBFC", border: `1px dashed ${theme.app.border}`, borderRadius: 8, padding: "9px 11px", margin: "2px 6px 6px 47px", "& svg": { fontSize: 18, color: theme.app.textTertiary } },
  hint: { color: theme.app.textTertiary, fontSize: 12, padding: theme.spacing(2, 0), textAlign: "center" },
  // resultado de busca: chip do nó + caminho + valor
  searchRow: { display: "flex", alignItems: "center", gap: 9, padding: "6px 6px", borderRadius: 8, cursor: "pointer", "&:hover": { background: theme.app.hover } },
  searchMain: { flex: 1, minWidth: 0 },
  searchKey: { fontFamily: theme.app.mono, fontSize: 12.5, fontWeight: 400, color: theme.app.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  searchPath: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  searchVal: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.ok, background: "#EAF6EE", borderRadius: 5, padding: "2px 6px", maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0 },
}));

const isObj = v => v && typeof v === "object";
const isBlank = v => v === undefined || v === null || v === "";
const fmtVal = v => {
  if (v == null) return "";
  if (typeof v === "object") return Array.isArray(v) ? `lista · ${v.length}` : "objeto";
  return String(v);
};
const fmtDate = iso => {
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

// achata um valor em FOLHAS (sem aninhamento): objeto/array vazio também é folha
const flattenLeaves = (val, prefix, push) => {
  if (!isObj(val)) { push(prefix, val); return; }
  const entries = Array.isArray(val) ? val.map((v, i) => [String(i), v]) : Object.entries(val);
  if (!entries.length) { push(prefix, val); return; }
  entries.forEach(([k, v]) => flattenLeaves(v, `${prefix}.${k}`, push));
};

// fallback p/ chamadas antigas sem `groups`: deriva grupos rasos do scope cru
const deriveFromScope = scope => {
  if (!scope) return [];
  const out = [];
  const steps = scope.steps || {};
  Object.keys(steps).forEach(k => {
    if (!isObj(steps[k]) || !Object.keys(steps[k]).length) return;
    out.push({ id: k, title: k, verbKey: k, category: "advanced", tag: k, prefix: `steps.${k}`, known: [], value: steps[k], dynamic: true });
  });
  return out;
};

const VariablePicker = ({ open, groups, runAt, scope, onClose, onPick }) => {
  const classes = useStyles();
  const [q, setQ] = useState("");
  const [openSet, setOpenSet] = useState(() => new Set()); // nós expandidos (vazio = todos colapsados)

  const all = groups && groups.length ? groups : deriveFromScope(scope);
  const ql = q.trim().toLowerCase();
  const rel = (g, token) => (token.startsWith(g.prefix + ".") ? token.slice(g.prefix.length + 1) : token);

  // FOLHAS de um nó: achata o valor real + os campos conhecidos (exemplos), dedup
  const fieldsOf = g => {
    const leaves = [];
    const seen = new Set();
    const add = (token, value) => { if (!seen.has(token)) { seen.add(token); leaves.push({ key: rel(g, token), token, value }); } };
    if (g.value) flattenLeaves(g.value, g.prefix, add);
    (g.known || []).forEach(k => { if (!seen.has(k.token)) flattenLeaves(k.example, k.token, add); });
    return leaves;
  };

  const toggle = id => setOpenSet(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // BUSCA: achata todas as folhas e casa por chave/caminho OU valor
  const results = ql
    ? all.flatMap(g => fieldsOf(g).filter(f => `${f.key} ${f.token} ${fmtVal(f.value)}`.toLowerCase().includes(ql)).map(f => ({ g, ...f })))
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ style: { maxHeight: "85vh", display: "flex", flexDirection: "column" } }}>
      <DialogTitle>{i18n.t("vars.title")}</DialogTitle>
      <DialogContent style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        {runAt && (
          <div className={classes.note}>
            <span className={classes.noteDot} /> Exemplos da última execução · {fmtDate(runAt)}
          </div>
        )}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Buscar por campo ou valor (ex.: João)"
          value={q}
          onChange={e => setQ(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />

        <div className={classes.list}>
          {results ? (
            results.length === 0 ? (
              <Typography className={classes.hint}>{i18n.t("vars.noMatch")}</Typography>
            ) : (
              results.map((r, i) => (
                <div key={i} className={classes.searchRow} onClick={() => onPick(r.token)}>
                  <NodeIcon verbKey={r.g.verbKey} triggerType={r.g.id === "trigger" ? r.g.tag : undefined} category={r.g.category} color={r.g.accent} size={26} chip />
                  <div className={classes.searchMain}>
                    <div className={classes.searchKey}>{r.key}</div>
                    <div className={classes.searchPath}>{r.token}</div>
                  </div>
                  {!isBlank(r.value) && !isObj(r.value) && <span className={classes.searchVal}>{fmtVal(r.value)}</span>}
                </div>
              ))
            )
          ) : all.length === 0 ? (
            <Typography className={classes.hint}>{i18n.t("vars.empty")}</Typography>
          ) : (
            all.map(g => {
              const isOpen = openSet.has(g.id);
              const fields = isOpen ? fieldsOf(g) : null;
              return (
                <div key={g.id}>
                  <div className={classes.groupHead} onClick={() => toggle(g.id)}>
                    <span className={classes.caret}>{isOpen ? "▾" : "▸"}</span>
                    <NodeIcon verbKey={g.verbKey} triggerType={g.id === "trigger" ? g.tag : undefined} category={g.category} color={g.accent} size={26} chip />
                    <span className={classes.groupTitle}>{g.title}</span>
                    <span className={classes.groupTag}>{g.tag}</span>
                  </div>
                  {isOpen &&
                    (fields.length === 0 ? (
                      <div className={classes.invite}>
                        <PlayIcon />
                        Rode o fluxo uma vez para ver os campos reais aqui.
                      </div>
                    ) : (
                      fields.map(f => (
                        <div key={f.token} className={classes.leaf} onClick={() => onPick(f.token)}>
                          <span className={classes.key}>{f.key}</span>
                          {isBlank(f.value) ? (
                            <span className={classes.empty}>— sem dados nesta execução</span>
                          ) : (
                            <span className={classes.val}>{fmtVal(f.value)}</span>
                          )}
                        </div>
                      ))
                    ))}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VariablePicker;
